// Document standards tab: external standards cited by the document with designation, title, publisher, citation count and the citing clauses.
import { useQuery } from "@tanstack/react-query";
import { BookMarkedIcon } from "lucide-react";
import { useState } from "react";

import type { StandardOut } from "@/api/__generated__";
import { catalogListStandardsOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { ClauseLink } from "@/components/common/ClauseLink";
import { EmptyState } from "@/components/common/EmptyState";
import { Mono } from "@/components/common/Mono";
import { ProblemState } from "@/components/common/ProblemState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber } from "@/lib/format";

const SHOWN_CITATIONS = 6;

function CitingClauses({ standard }: { standard: StandardOut }) {
  const [all, setAll] = useState(false);
  const shown = all ? standard.cited_by : standard.cited_by.slice(0, SHOWN_CITATIONS);
  const hidden = standard.cited_by.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
      {shown.map((clause) => (
        <ClauseLink key={clause.clause_uid} clauseUid={clause.clause_uid} number={clause.number} />
      ))}
      {hidden > 0 ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto text-xs"
          onClick={() => {
            setAll(true);
          }}
        >
          {formatNumber(hidden)} more
        </Button>
      ) : null}
      {standard.citation_count > standard.cited_by.length ? (
        <span className="text-xs text-muted-foreground">
          (first {formatNumber(standard.cited_by.length)} of {formatNumber(standard.citation_count)})
        </span>
      ) : null}
    </div>
  );
}

export function StandardsTab({ docUid }: { docUid: string }) {
  const standards = useQuery(catalogListStandardsOptions({ path: { doc_uid: docUid } }));
  if (standards.isError) {
    return (
      <Card>
        <ProblemState
          problem={standards.error}
          retry={() => {
            void standards.refetch();
          }}
        />
      </Card>
    );
  }
  if (!standards.isPending && standards.data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={BookMarkedIcon}
          title="No external standard is cited"
          description="Citations of standards are found from clause references during ingestion."
        />
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <Table aria-label="Cited standards">
        <TableHeader>
          <TableRow>
            <TableHead>Designation</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Publisher</TableHead>
            <TableHead className="text-right">Citations</TableHead>
            <TableHead className="w-[42%]">Cited by</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standards.isPending
            ? [0, 1, 2, 3].map((index) => (
                <TableRow key={index} aria-hidden>
                  {[0, 1, 2, 3, 4].map((cell) => (
                    <TableCell key={cell}>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : standards.data.map((standard) => (
                <TableRow key={standard.standard_id}>
                  <TableCell>
                    <Mono tone="ink" className="font-semibold">
                      {standard.designation}
                    </Mono>
                  </TableCell>
                  <TableCell className="text-ink-2">{standard.title ?? "–"}</TableCell>
                  <TableCell>{standard.publisher ?? "–"}</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatNumber(standard.citation_count)}
                  </TableCell>
                  <TableCell>
                    <CitingClauses standard={standard} />
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </Card>
  );
}
